/* Owns the route tree of PANEL.md §2, in react-router 7 data mode.
   `createBrowserRouter` is called once at module scope; `RouterProvider` comes
   from `react-router/dom` in `main.tsx`, everything else from `react-router`.

   Page components are code-split. They are resolved through an
   `import.meta.glob` registry rather than a literal `import()` per route,
   because the feature directories land across several work packages: the
   registry is the build's real inventory, so a route whose module has not
   shipped yet degrades to a readable error instead of breaking the whole
   bundle. Every path below is exactly the one PANEL.md §14 fixes, so a module
   appears in the registry the moment its file exists — no wiring change. */

import type { ComponentType, ReactNode } from 'react'
import { createBrowserRouter, Navigate, Outlet, isRouteErrorResponse, useRouteError, type RouteObject } from 'react-router'

import { ErrorState } from './components/ui/ErrorState.js'
import { AppShell } from './app/AppShell.js'
import { RequireAuth } from './app/RequireAuth.js'
import { RequirePlatformAdmin, RequireRole } from './app/RequireRole.js'
import { ServerLayout } from './app/ServerLayout.js'
import { useT } from './i18n/index.js'

const pageModules: Record<string, () => Promise<unknown>> = import.meta.glob([
  './features/**/*.tsx',
  '!./features/**/*.test.tsx',
])

function ModuleUnavailable({ modulePath }: { modulePath: string }): ReactNode {
  const t = useT()
  return (
    <ErrorState title={t('common.errorTitle')} description={modulePath} code="NOT_FOUND" />
  )
}

function isComponent(value: unknown): value is ComponentType {
  return typeof value === 'function'
}

/**
 * Builds the `lazy` loader for one page. The named export is preferred over
 * `default` because PANEL.md names every page component explicitly, and a file
 * that exports the wrong name should fail loudly here rather than render blank.
 */
function page(modulePath: string, exportName: string): () => Promise<{ Component: ComponentType }> {
  return async () => {
    const load = pageModules[modulePath]
    if (!load) return { Component: () => <ModuleUnavailable modulePath={modulePath} /> }
    const loaded = (await load()) as Record<string, unknown>
    const exported = loaded[exportName] ?? loaded['default']
    if (!isComponent(exported)) return { Component: () => <ModuleUnavailable modulePath={modulePath} /> }
    return { Component: exported }
  }
}

/** Catches a thrown render or navigation error anywhere under the shell. */
function RouteErrorBoundary(): ReactNode {
  const error = useRouteError()
  const t = useT()
  const description = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : undefined
  return (
    <div className="p-4 lg:p-6">
      <ErrorState
        title={t('common.errorTitle')}
        {...(description ? { description } : {})}
        onRetry={() => window.location.reload()}
      />
    </div>
  )
}

/** `/s/:serverId/*`. Permissions are grouped so each gate is declared once. */
const serverRoutes: RouteObject[] = [
  { index: true, element: <Navigate to="dashboard" replace /> },
  {
    element: <RequireRole permission="server.read" />,
    children: [
      { path: 'dashboard', lazy: page('./features/dashboard/DashboardPage.tsx', 'DashboardPage') },
      { path: 'global-bans', lazy: page('./features/global-bans/GlobalBansPage.tsx', 'GlobalBansPage') },
      { path: 'whitelist', lazy: page('./features/whitelist/WhitelistPage.tsx', 'WhitelistPage') },
      { path: 'commands', lazy: page('./features/commands/CommandsPage.tsx', 'CommandsPage') },
      { path: 'discord', lazy: page('./features/discord/DiscordPage.tsx', 'DiscordPage') },
      { path: 'team', lazy: page('./features/team/TeamPage.tsx', 'TeamPage') },
      { path: 'settings', lazy: page('./features/settings/ServerSettingsPage.tsx', 'ServerSettingsPage') },
    ],
  },
  {
    element: <RequireRole permission="detections.read" />,
    children: [
      { path: 'live', lazy: page('./features/live/LiveFeedPage.tsx', 'LiveFeedPage') },
      {
        path: 'detections',
        lazy: page('./features/detections/DetectionsPage.tsx', 'DetectionsPage'),
        // The drawer is a child route so the list stays mounted underneath and
        // Escape / the backdrop can pop the route rather than only hide it.
        children: [
          { path: ':detectionId', lazy: page('./features/detections/DetectionDrawer.tsx', 'DetectionDrawer') },
        ],
      },
    ],
  },
  {
    element: <RequireRole permission="players.read" />,
    children: [
      { path: 'map', lazy: page('./features/map/LiveMapPage.tsx', 'LiveMapPage') },
      { path: 'players', lazy: page('./features/players/PlayersPage.tsx', 'PlayersPage') },
      { path: 'players/:playerId', lazy: page('./features/players/PlayerDetailPage.tsx', 'PlayerDetailPage') },
    ],
  },
  {
    element: <RequireRole permission="bans.read" />,
    children: [
      {
        path: 'bans',
        lazy: page('./features/bans/BansPage.tsx', 'BansPage'),
        children: [{ path: ':banId', lazy: page('./features/bans/BanDrawer.tsx', 'BanDrawer') }],
      },
    ],
  },
  {
    element: <RequireRole permission="screenshots.read" />,
    children: [{ path: 'screenshots', lazy: page('./features/screenshots/ScreenshotsPage.tsx', 'ScreenshotsPage') }],
  },
  {
    element: <RequireRole permission="config.read" />,
    children: [{ path: 'config', lazy: page('./features/config/ConfigPage.tsx', 'ConfigPage') }],
  },
  {
    element: <RequireRole permission="license.manage" />,
    children: [{ path: 'licenses', lazy: page('./features/licenses/LicensesPage.tsx', 'LicensesPage') }],
  },
  {
    element: <RequireRole permission="audit.read" />,
    children: [{ path: 'audit', lazy: page('./features/audit/AuditPage.tsx', 'AuditPage') }],
  },
]

export const routes: RouteObject[] = [
  /* Public marketing, outside `RequireAuth`.
   *
   * `/` used to live inside the authenticated shell and redirect to
   * `/servers`, which meant the only thing a visitor without an account could
   * ever see was the login form. It is a real page now, and it does NOT bounce a
   * signed-in owner away either: the header offers the panel instead of a
   * sign-in button, so one URL works for both. */
  { path: '/', lazy: page('./features/marketing/LandingPage.tsx', 'LandingPage') },
  { path: '/pricing', lazy: page('./features/marketing/PricingPage.tsx', 'PricingPage') },

  // Public: no shell, no session. `next=` round-trips through the login form.
  { path: '/login', lazy: page('./features/auth/LoginPage.tsx', 'LoginPage') },
  { path: '/register', lazy: page('./features/auth/RegisterPage.tsx', 'RegisterPage') },
  { path: '/forgot', lazy: page('./features/auth/ForgotPasswordPage.tsx', 'ForgotPasswordPage') },
  { path: '/reset/:token', lazy: page('./features/auth/ResetPasswordPage.tsx', 'ResetPasswordPage') },
  {
    // Authenticated but shell-less: the invite page has no server context yet.
    element: (
      <RequireAuth>
        <Outlet />
      </RequireAuth>
    ),
    children: [{ path: '/invite/:token', lazy: page('./features/auth/AcceptInvitePage.tsx', 'AcceptInvitePage') }],
  },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    ErrorBoundary: RouteErrorBoundary,
    children: [
      /* No index route here.
       *
       * The shell is a PATHLESS layout route, so an index child of it matches
       * `/` — the same path the landing page now claims. Leaving both in place
       * let the shell win the match and `/` went straight back to the server
       * list, which is exactly the behaviour the landing page exists to end.
       * Every page under this shell has an explicit path, so nothing else
       * needed the index. */
      { path: 'servers', lazy: page('./features/servers/ServersPage.tsx', 'ServersPage') },
      { path: 'servers/new', lazy: page('./features/servers/CreateServerWizard.tsx', 'CreateServerWizard') },
      { path: 'lookup', lazy: page('./features/lookup/LookupPage.tsx', 'LookupPage') },
      { path: 'account', lazy: page('./features/account/AccountPage.tsx', 'AccountPage') },
      { path: 'account/api-keys', lazy: page('./features/account/ApiKeysPage.tsx', 'ApiKeysPage') },
      { path: 'account/plan', lazy: page('./features/account/PlanPage.tsx', 'PlanPage') },
      {
        path: 'admin/global-bans',
        element: <RequirePlatformAdmin />,
        children: [{ index: true, lazy: page('./features/admin/AdminGlobalBansPage.tsx', 'AdminGlobalBansPage') }],
      },
      { path: 's/:serverId', Component: ServerLayout, children: serverRoutes },
      { path: '*', lazy: page('./features/misc/NotFoundPage.tsx', 'NotFoundPage') },
    ],
  },
]

export const router = createBrowserRouter(routes)
